import { LitElement, html, css } from 'lit'
import { StoreMixin, store } from '../store.js'
import { navigate } from '../router.js'
import { api, clearAccessToken } from '../api.js'
import { brandLogo } from './brand-logo.js'

// The application header (BrandExtract design system): a sticky eggplant bar with
// the logotype + "Support" eyebrow, pill navigation, and an account menu. Renders
// only when a user is present. Agents also get an Admin section in the menu.
class AppNav extends StoreMixin(LitElement) {
  static properties = { _menuOpen: { state: true }, _path: { state: true } }

  static styles = css`
    :host { display: block; font-family: var(--font-sans); position: sticky; top: 0; z-index: 20; }
    nav {
      background: var(--eggplant-500); color: var(--neutral-50);
      display: flex; align-items: center; gap: 40px; padding: 0 40px; height: 72px;
    }
    @media (max-width: 620px) { nav { gap: 20px; padding: 0 20px; } .links a { padding: 8px 12px; } }
    .brand { display: flex; align-items: center; gap: 14px; color: var(--white); cursor: pointer; }
    .brand svg { height: 34px; width: auto; }
    .tag {
      font-family: var(--font-eyebrow); font-size: 11px; font-weight: 700; letter-spacing: 0.16em;
      text-transform: uppercase; color: var(--chartreuse-500); padding-left: 14px;
      border-left: 1px solid rgba(255,255,255,0.22); position: relative; top: 5px;
    }
    .links { display: flex; gap: 6px; margin-right: auto; }
    .links a {
      padding: 8px 16px; border-radius: 999px; font-size: 15px; font-weight: 500;
      color: rgba(255,255,255,0.72); background: transparent; cursor: pointer;
      text-decoration: none; transition: color .15s ease, background .15s ease;
    }
    .links a:hover { color: var(--white); }
    .links a.active { color: var(--white); font-weight: 700; background: rgba(255,255,255,0.14); }

    .account { position: relative; }
    .avatar {
      display: flex; align-items: center; gap: 10px; cursor: pointer;
      background: rgba(255,255,255,0.1); border: none; padding: 5px 14px 5px 5px;
      border-radius: 999px; color: var(--white); font: inherit;
    }
    .avatar .initial {
      width: 30px; height: 30px; border-radius: 50%; display: grid; place-items: center;
      background: var(--chartreuse-500); color: var(--eggplant-500); font-weight: 800; font-size: 13px;
    }
    .avatar .name { font-size: 14px; font-weight: 500; }

    .menu {
      position: absolute; right: 0; top: 52px; min-width: 210px; background: var(--white);
      color: var(--eggplant-500); border-radius: 8px; overflow: hidden;
      box-shadow: 0 10px 40px rgba(51,35,75,0.22); border: 1px solid var(--neutral-100); z-index: 50;
    }
    .menu .who { padding: 14px 16px 4px; font-family: var(--font-eyebrow); font-size: 10px;
                 letter-spacing: 0.12em; text-transform: uppercase; color: var(--neutral-500); }
    .menu .name { padding: 0 16px 12px; font-weight: 700; }
    .menu .section { padding: 12px 16px 4px; font-family: var(--font-eyebrow); font-size: 10px;
                     letter-spacing: 0.12em; text-transform: uppercase; color: var(--neutral-500); }
    .menu button { display: block; width: 100%; box-sizing: border-box; text-align: left;
                   padding: 10px 16px; background: none; border: none; font: inherit; cursor: pointer;
                   color: var(--eggplant-500); }
    .menu button:hover { background: var(--neutral-50); }
    .menu button.active { font-weight: 700; background: var(--neutral-50); }
    .menu hr { border: none; border-top: 1px solid var(--neutral-100); margin: 4px 0; }
  `

  constructor() {
    super()
    this._path = window.location.pathname
    this._onNav = () => { this._path = window.location.pathname }
    // Close the account menu on any click that isn't inside the dropdown itself.
    // composedPath sees through the shadow DOM; the toggle click stops propagation
    // so opening the menu doesn't immediately close it.
    this._onDocClick = (e) => {
      if (!this._menuOpen) return
      const menu = this.renderRoot.querySelector('.menu')
      if (menu && e.composedPath().includes(menu)) return
      this._menuOpen = false
    }
  }

  connectedCallback() {
    super.connectedCallback()
    window.addEventListener('app:navigate', this._onNav)
    window.addEventListener('popstate', this._onNav)
    window.addEventListener('click', this._onDocClick)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('app:navigate', this._onNav)
    window.removeEventListener('popstate', this._onNav)
    window.removeEventListener('click', this._onDocClick)
  }

  // A link is current when the path equals it or sits beneath it (e.g. /tickets/42,
  // /kb/articles/…). `/` is treated as Tickets since that's where the root resolves.
  _active(path) {
    const p = this._path === '/' ? '/tickets' : this._path
    return p === path || p.startsWith(path + '/')
  }

  // Up to two initials for the avatar (e.g. "Jason Wolter" → JW).
  _initials(user) {
    const src = (user.fullName || user.email || '?').trim()
    return src.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()
  }

  _toggle(e) { e.stopPropagation(); this._menuOpen = !this._menuOpen }

  _go(path) { this._menuOpen = false; navigate(path) }

  async _logout() {
    this._menuOpen = false
    try { await api.post('/auth/logout', {}) } catch {}
    clearAccessToken()
    store.clear()
    navigate('/login')
  }

  render() {
    const { user } = this.appState
    if (!user) return html``
    return html`
      <nav>
        <div class="brand" @click=${() => navigate('/')}>
          ${brandLogo(34)}
          <span class="tag">Support</span>
        </div>
        <span class="links">
          <a class=${this._active('/tickets') ? 'active' : ''} @click=${() => navigate('/tickets')}>Tickets</a>
          ${user.role === 'agent' ? html`
            <a class=${this._active('/triage') ? 'active' : ''} @click=${() => navigate('/triage')}>Triage</a>` : ''}
          <a class=${this._active('/kb') ? 'active' : ''} @click=${() => navigate('/kb')}>Knowledge Base</a>
        </span>
        <div class="account">
          <button class="avatar" @click=${this._toggle}>
            <span class="initial">${this._initials(user)}</span>
            <span class="name">${user.fullName || user.email}</span>
          </button>
          ${this._menuOpen ? html`
            <div class="menu">
              <div class="who">Signed in as</div>
              <div class="name">${user.fullName || user.email}</div>
              <hr />
              ${user.role === 'agent' ? html`
                <div class="section">Admin</div>
                <button class=${this._active('/clients') ? 'active' : ''} @click=${() => this._go('/clients')}>Clients</button>
                <button class=${this._active('/groups') ? 'active' : ''} @click=${() => this._go('/groups')}>Groups</button>
                <button class=${this._active('/agents') ? 'active' : ''} @click=${() => this._go('/agents')}>Agents</button>
                <button class=${this._active('/reports') ? 'active' : ''} @click=${() => this._go('/reports')}>Reports</button>
                <button class=${this._active('/subscriptions') ? 'active' : ''} @click=${() => this._go('/subscriptions')}>Subscriptions</button>
                <button class=${this._active('/connections') ? 'active' : ''} @click=${() => this._go('/connections')}>Connected apps</button>
                <button class=${this._active('/api-keys') ? 'active' : ''} @click=${() => this._go('/api-keys')}>API keys</button>
                <hr />` : ''}
              <button @click=${this._logout}>Sign out</button>
            </div>` : ''}
        </div>
      </nav>
    `
  }
}

customElements.define('app-nav', AppNav)
